import { type Filter, type Query } from "@cubejs-client/core"
import { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import type { TReportChartConfig, TReportSeries } from "./components/Report"

export type TReportSection = {
  id: string
  title: string
  description?: string
  query: Query
  chart: TReportChartConfig
  columns: TableVisualizationColumn[]
  /** Tag category slug this section groups by (taxonomy sections only). */
  tagSlug?: string
}

export const VIEW = "creative_taxonomy_report"
const m = (name: string) => `${VIEW}.${name}`

// Cube returns `null` for a taxonomy dimension when the ad has no row in
// company_creative_ad_tags at all, or has one whose column for this vertical is
// empty. Both collapse into a single group that the table used to render as a
// bare "-", indistinguishable from a formatting glitch. Label it so the row
// reads as a real bucket, and so row-click can recognise it and translate the
// value back into a `notSet` filter (an `equals` on this string matches
// nothing). Keep this in sync with the drawer's filter in TaxonomyGroupAds.
export const UNTAGGED_LABEL = "(untagged)"

/** Cube reports "no value for this vertical" as null; treat "" the same way. */
export const isEmptyDimensionValue = (value: unknown) =>
  value === null || value === undefined || value === ""

// Tag category slug (Postgres `TagCategory.slug`) -> cube dimension on the
// view, for the six system categories the warehouse promotes to their own
// column in media_metrics_paid_media_ad_tags (see company_creative_ad_tags).
// They line up except where the cube dimension dropped the underscore.
export const CUBE_DIMENSION_BY_SLUG: Record<string, string> = {
  value_prop: "valueprop",
  format: "format",
  style: "style",
  hook: "hook",
  persona: "persona",
  length: "length",
}

// Company-defined categories have no column of their own — they live inside
// the ad's `tags_json`. The cube exposes one generic pair for them: a section
// filters CUSTOM_TAG_SLUG_DIMENSION equal to the category's slug and groups by
// CUSTOM_TAG_VALUE_DIMENSION, which the cube resolves to that slug's value per
// ad (see company_creative_ad_tags.custom_tag_value).
export const CUSTOM_TAG_SLUG_DIMENSION = "custom_tag_slug"
export const CUSTOM_TAG_VALUE_DIMENSION = "custom_tag_value"

/**
 * The subset of a tag category the report needs. The API's `TagCategory`
 * satisfies this; tests and the loading-time fallback build it by hand.
 */
export type TTaxonomyCategory = {
  slug: string
  label: string
}

/**
 * System-default categories (mirrors SYSTEM_CATEGORIES in the API's
 * tag-categories.service, in declared order). Used only until the company's
 * own list arrives, so the page renders the familiar sections immediately
 * instead of flashing an empty report — the API always returns these six
 * (materialised or virtual) ahead of any custom categories, so the swap is
 * label/order-only.
 */
export const DEFAULT_TAXONOMY_CATEGORIES: TTaxonomyCategory[] = [
  { slug: "value_prop", label: "Value Prop" },
  { slug: "format", label: "Format" },
  { slug: "style", label: "Style" },
  { slug: "hook", label: "Hook" },
  { slug: "persona", label: "Persona" },
  { slug: "length", label: "Length" },
]

export type TTaxonomyVertical = {
  /** Tag category slug, e.g. "value_prop". */
  slug: string
  /** Company-facing label (renamable per company), e.g. "Value Prop". */
  label: string
  /**
   * "column": a system category with its own cube dimension.
   * "custom": a company-defined category read out of tags_json through the
   * generic custom_tag_* pair.
   */
  kind: "column" | "custom"
  /** Cube dimension (prefix-stripped) the section groups by. */
  dimKey: string
  /** Section-level cube filters that select this category (custom only). */
  filters: Filter[]
}

/**
 * Report verticals for a company: one per tag category, in API order. A
 * system category groups by its promoted column (a renamed one keeps the
 * column but takes the new label); a company-defined category groups by the
 * generic custom-tag dimension, narrowed to its slug by a section filter.
 */
export const getTaxonomyVerticals = (
  categories: TTaxonomyCategory[],
): TTaxonomyVertical[] =>
  categories.map(({ slug, label }) => {
    const column = CUBE_DIMENSION_BY_SLUG[slug]
    return column
      ? { slug, label, kind: "column", dimKey: column, filters: [] }
      : {
          slug,
          label,
          kind: "custom",
          dimKey: CUSTOM_TAG_VALUE_DIMENSION,
          filters: [
            {
              member: m(CUSTOM_TAG_SLUG_DIMENSION),
              operator: "equals",
              values: [slug],
            },
          ],
        }
  })

/**
 * How many of the drawer's ads already carry a tag for this category in the
 * app but have not reached the warehouse yet. Keyed by the category's slug —
 * the same key the bulk tag-assignment response uses — so it works for
 * custom categories exactly like system ones.
 */
export const countPendingSync = (
  liveTags: Record<string, Record<string, unknown[]>> | undefined,
  slug: string | undefined,
) => {
  if (!liveTags || !slug) return 0
  return Object.values(liveTags).filter(
    byCategory => (byCategory?.[slug]?.length ?? 0) > 0,
  ).length
}

// Measures shared by the taxonomy + platform breakdowns.
const BREAKDOWN_MEASURES = [
  m("spend"),
  m("brand_value_over_spend"),
  m("revenue_from_search_roas"),
  m("ad_count"),
  m("likes"),
  m("comments"),
  m("shares"),
  m("saves"),
  m("follows"),
]

// Metric columns (prefix-stripped keys) for the default (no-preset) breakdowns.
const DEFAULT_BREAKDOWN_COLUMNS: TableVisualizationColumn[] = [
  { key: "ad_count", header: "# Ads", type: "number" },
  { key: "spend", header: "Spend", type: "currency" },
  { key: "brand_value_over_spend", header: "Brand ROAS", type: "coef" },
  { key: "revenue_from_search_roas", header: "Search ROAS", type: "coef" },
  { key: "likes", header: "Likes", type: "number" },
  { key: "comments", header: "Comments", type: "number" },
  { key: "shares", header: "Shares", type: "number" },
  { key: "saves", header: "Saves", type: "number" },
  { key: "follows", header: "Follows", type: "number" },
]

// Quick-select presets mirroring the Creative Reports v2 TOF/DR shortcuts,
// mapped to this view's members (purchase_value_over_spend ->
// short_term_revenue_roas, total_value -> long_term_revenue_roas). An active
// preset swaps the breakdown sections' measures/columns/chart series and
// applies a page-wide initiative_type filter.
export type TTaxonomyPreset = {
  key: string
  label: string
  /** initiative_type "contains" value applied as a page filter when active. */
  initiativeContains: string
  /** Measure names (unprefixed) requested for the breakdown sections. */
  measures: string[]
  /** Metric columns (after the dimension column) for the breakdown tables. */
  columns: TableVisualizationColumn[]
  /** Chart series for the breakdown sections. */
  series: TReportSeries[]
}

export const TAXONOMY_PRESETS: TTaxonomyPreset[] = [
  {
    key: "tof",
    label: "TOF",
    initiativeContains: "generation",
    measures: [
      "ad_count",
      "spend",
      "brand_value_agg",
      "brand_value_over_spend",
      "modeled_brand_value_over_spend",
      "estimated_d180_brand_value",
      "follows",
      "shares",
      "comments",
    ],
    columns: [
      { key: "ad_count", header: "# Ads", type: "number" },
      { key: "spend", header: "Spend", type: "currency" },
      { key: "brand_value_agg", header: "Brand Value", type: "currency" },
      { key: "brand_value_over_spend", header: "Brand ROAS", type: "coef" },
      {
        key: "modeled_brand_value_over_spend",
        header: "Modeled bROAS",
        type: "coef",
      },
      {
        key: "estimated_d180_brand_value",
        header: "Modeled Brand Value",
        type: "currency",
      },
      { key: "follows", header: "Follows", type: "number" },
      { key: "shares", header: "Shares", type: "number" },
      { key: "comments", header: "Comments", type: "number" },
    ],
    series: [
      { key: "spend", label: "Spend", numberType: "currency" },
      {
        key: "modeled_brand_value_over_spend",
        label: "Modeled bROAS",
        numberType: "coef",
        axis: "right",
      },
    ],
  },
  {
    key: "dr",
    label: "DR",
    initiativeContains: "capture",
    measures: [
      "ad_count",
      "spend",
      "modeled_revenue",
      "purchases",
      "short_term_revenue_roas",
      "long_term_revenue_roas",
    ],
    columns: [
      { key: "ad_count", header: "# Ads", type: "number" },
      { key: "spend", header: "Spend", type: "currency" },
      { key: "modeled_revenue", header: "Modeled Revenue", type: "currency" },
      { key: "purchases", header: "Purchase Revenue", type: "currency" },
      {
        key: "short_term_revenue_roas",
        header: "Purchase ROAS",
        type: "coef",
      },
      {
        key: "long_term_revenue_roas",
        header: "Total Value ROAS",
        type: "coef",
      },
    ],
    series: [
      { key: "spend", label: "Spend", numberType: "currency" },
      {
        key: "modeled_revenue",
        label: "Modeled Revenue",
        numberType: "currency",
      },
      {
        key: "short_term_revenue_roas",
        label: "ROAS",
        numberType: "coef",
        axis: "right",
      },
    ],
  },
]

const breakdownMeasures = (preset?: TTaxonomyPreset) =>
  preset ? preset.measures.map(m) : BREAKDOWN_MEASURES

const breakdownSeries = (preset?: TTaxonomyPreset): TReportSeries[] =>
  preset?.series ?? [{ key: "brand_value_over_spend", label: "Brand ROAS" }]

// Table columns shared by the taxonomy breakdowns.
const breakdownColumns = (
  dimKey: string,
  dimHeader: string,
  preset?: TTaxonomyPreset,
): TableVisualizationColumn[] => [
  { key: dimKey, header: dimHeader, type: "text" },
  ...(preset?.columns ?? DEFAULT_BREAKDOWN_COLUMNS),
]

// Every section on the page only reports Meta + TikTok + Pinterest
// (media_channel values are always lowercase).
const TAXONOMY_MEDIA_CHANNELS = ["meta", "tiktok", "pinterest"]

const MEDIA_CHANNEL_FILTER = {
  member: m("media_channel"),
  operator: "equals" as const,
  values: TAXONOMY_MEDIA_CHANNELS,
}

// One taxonomy-vertical section per company tag category (see
// getTaxonomyVerticals). Keyed by slug: custom categories all share one cube
// dimension, so the dimension alone would not be unique.
const taxonomySection = (
  { slug, dimKey, label, filters }: TTaxonomyVertical,
  preset?: TTaxonomyPreset,
): TReportSection => ({
  id: `taxonomy-${slug}`,
  title: label,
  description: `Performance by ${label.toLowerCase()}.`,
  tagSlug: slug,
  query: {
    dimensions: [m(dimKey)],
    measures: breakdownMeasures(preset),
    filters: [MEDIA_CHANNEL_FILTER, ...filters],
    order: { [m("spend")]: "desc" },
  },
  chart: {
    type: "bar",
    xKey: dimKey,
    numberType: "coef",
    series: breakdownSeries(preset),
  },
  columns: breakdownColumns(dimKey, label, preset),
})

// Sections for the page: the platform overview, then one breakdown per
// taxonomy vertical of the company's tag categories. An active preset reshapes
// every breakdown's measures, columns, and chart series.
export const getReportSections = (
  preset: TTaxonomyPreset | undefined,
  categories: TTaxonomyCategory[],
): TReportSection[] => [
  // --- Campaign / platform breakdown (overview) ---
  {
    id: "platform-breakdown",
    title: "Platform Breakdown",
    description:
      "Brand ROAS and spend by platform. Benchmarks differ per platform (FB/TikTok/YouTube 3.0+, Instagram 1.5+).",
    query: {
      dimensions: [m("media_channel")],
      measures: breakdownMeasures(preset),
      filters: [MEDIA_CHANNEL_FILTER],
      order: { [m("spend")]: "desc" },
    },
    chart: {
      type: "bar",
      xKey: "media_channel",
      numberType: "coef",
      series: breakdownSeries(preset),
    },
    columns: breakdownColumns("media_channel", "Platform", preset),
  },

  // --- Taxonomy verticals ---
  ...getTaxonomyVerticals(categories).map(vertical =>
    taxonomySection(vertical, preset),
  ),
]
